import React, { useEffect, useMemo } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./Cards.css"; // Ensure this file contains your animation keyframes and other styles
import { isSummaryDataReady } from "./summaryCardReadiness";
import {
  buildSummaryCards,
  POWER_FLOW_ARMS,
  flowGradient,
} from "./cardFlowRows";
import CardFlowRows from "./CardFlowRows.jsx";

const toTestId = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const StatusCardSkeleton = () => (
  <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
    <div className="status-card-grid" data-testid="summary-cards-skeleton">
      {/* Transfer Card Skeleton */}
      <div className="status-card-grid-item transfer">
        <div className="status-card-content">
          <div className="graph-name d-flex">
            <div className="sc-card-title">
              <Skeleton
                width={120}
                height={24}
                style={{ marginBottom: "8px" }}
              />
            </div>
            <div className="sc-card-icon">
              <div className="icon-circle transfer-bg d-flex justify-content-center align-items-center">
                <Skeleton width={24} height={24} circle />
              </div>
            </div>
          </div>
          <div className="cost d-flex justify-content-center">
            <Skeleton width={100} height={32} style={{ marginBottom: "8px" }} />
          </div>
          <div className="stats d-flex">
            <div className="stat-item-storage-left">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>
            <div className="stat-item-storage-right">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Production Card Skeleton */}
      <div className="status-card-grid-item production">
        <div className="status-card-content">
          <div className="graph-name d-flex">
            <div className="sc-card-title">
              <Skeleton
                width={120}
                height={24}
                style={{ marginBottom: "8px" }}
              />
            </div>
            <div className="sc-card-icon">
              <div className="icon-circle prod-bg d-flex justify-content-center align-items-center">
                <Skeleton width={24} height={24} circle />
              </div>
            </div>
          </div>
          <div className="cost d-flex justify-content-center">
            <Skeleton width={100} height={32} style={{ marginBottom: "8px" }} />
          </div>
          <div className="stats d-flex">
            <div className="stat-item-storage-left">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>
            <div className="stat-item-storage-right">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Consumption Card Skeleton */}
      <div className="status-card-grid-item consumption">
        <div className="status-card-content">
          <div className="graph-name d-flex">
            <div className="sc-card-title">
              <Skeleton
                width={120}
                height={24}
                style={{ marginBottom: "8px" }}
              />
            </div>
            <div className="sc-card-icon">
              <div className="icon-circle consum-bg d-flex justify-content-center align-items-center">
                <Skeleton width={24} height={24} circle />
              </div>
            </div>
          </div>
          <div className="cost d-flex justify-content-center">
            <Skeleton width={100} height={32} style={{ marginBottom: "8px" }} />
          </div>
          <div className="stats d-flex">
            <div className="stat-item-storage-left">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>
            <div className="stat-item-storage-right">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Storage Card Skeleton */}
      <div className="status-card-grid-item storage">
        <div className="status-card-content">
          <div className="graph-name d-flex">
            <div className="sc-card-title">
              <Skeleton
                width={120}
                height={24}
                style={{ marginBottom: "8px" }}
              />
            </div>
            <div className="sc-card-icon">
              <div className="icon-circle storage-bg d-flex justify-content-center align-items-center">
                <Skeleton width={24} height={24} circle />
              </div>
            </div>
          </div>
          <div className="cost d-flex justify-content-center">
            <Skeleton width={100} height={32} style={{ marginBottom: "8px" }} />
          </div>
          <div className="stats d-flex">
            <div className="stat-item-storage-left">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>
            <div className="stat-item-storage-right">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Center Box */}
      <div className="center-box">
        <div className="center-content"></div>
      </div>
      <div className="vertical-line">
        <div className="moving-bar-transfer vertical-bar"></div>
        <div className="moving-bar-production vertical-bar"></div>
      </div>
      <div className="vertical-line-btm"></div>

      <div className="horizontal-line">
        <div className="moving-bar-consumption horizontal-bar"></div>
        <div className="moving-bar-storage horizontal-bar"></div>
      </div>
      <div className="horizontal-line-btm"></div>
    </div>
  </SkeletonTheme>
);

export default function StatusCard({
  exportedData = {},
  exportedProductionData = {},
  exportedProductionSummaryData,
  exportedStorageData = {},
  exportedStorageSummaryData,
  exportedTransferData = {},
  exportedDemandChargeData,
  onCardHoverChange,
  onCardToggle,
  onCardPointerDown,
  summaryCardVisibility,
  chartDataReadyStates,
  shouldShowCards,
  hasMeaningfulData,
  interactive = true,
}) {
  /* Readiness is a property of the data, never of `interactive`.
   *
   * The gate used to read
   *   loading || (interactive && !shouldShowCards && !hasMeaningfulData)
   * which had two independent holes. SummaryPage renders this component with
   * interactive={false} for the Power Flow tab -- the only production render --
   * so the whole data half short-circuited away and the gate collapsed to a
   * bare one-second setTimeout with no connection to data at all. And even when
   * it did run, shouldShowCards is defined as `hasMeaningfulData && (any ready
   * state)`, so it implies hasMeaningfulData: the AND reduced to
   * `!hasMeaningfulData` and the ready states never entered into it. */
  const dataReady = isSummaryDataReady({
    chartDataReadyStates,
    shouldShowCards,
    hasMeaningfulData,
  });

  const productionCardData =
    exportedProductionSummaryData || exportedProductionData;
  const storageCardData = exportedStorageSummaryData || exportedStorageData;

  const summaryData = useMemo(
    () =>
      buildSummaryCards({
        exportedData,
        productionData: productionCardData,
        storageData: storageCardData,
        transferData: exportedTransferData,
        demandData: exportedDemandChargeData,
      }),
    [
      exportedData,
      productionCardData,
      storageCardData,
      exportedTransferData,
      exportedDemandChargeData,
    ],
  );

  /* ── The power-flow arrows ────────────────────────────────────────────────
   *
   * Each bar is a comet travelling along one arm of the diamond. Two things
   * have to agree for it to read correctly: the keyframes that move it, and the
   * gradient that puts its solid head at the leading edge.
   *
   * ── The convention, read off the two bars that were always right ─────────
   * Production and Consumption are hardcoded and have never been reported
   * wrong, so they define the house style: the gradient runs transparent to
   * solid TOWARD the direction of travel, i.e. solid = head, fading = tail.
   * Expressed as `linear-gradient(<angle>, transparent 0%, colour 100%)`, the
   * angle points at the head -- 0deg up, 90deg right, 180deg down, 270deg left.
   *
   * ── What was wrong ───────────────────────────────────────────────────────
   * Two independent defects, and both had to be fixed for the arrow to read
   * correctly:
   *
   *   1. Direction came from `cost > 0`. Import versus export is a fact about
   *      energy, not money. husky-2040-prod imports 12,622,852 kWh and exports
   *      0 -- unambiguously inward -- while its Transfer Cost is -$925,253,
   *      because importing costs money. `cost > 0` was false.
   *
   *   2. Transfer's two gradients were swapped against the convention above:
   *      the downward keyframe was paired with a solid-at-top gradient and the
   *      upward one with solid-at-bottom, so whichever branch ran, the comet's
   *      head trailed its own motion. That is what made the arrow look
   *      backwards even in the case where the motion was right. Storage's
   *      gradients were correctly paired; Transfer's alone were inverted.
   *
   * Direction now comes from cardFlowDirection(), which reads energy, and the
   * keyframes/gradient pairing lives in POWER_FLOW_ARMS in cardFlowRows.js --
   * one table, so the two halves cannot drift apart again.
   *
   * ── animation-direction is deliberately not set ──────────────────────────
   * The old code set `bar.style.animationDirection` and then `bar.style.
   * animation`, the shorthand, which resets every animation-* longhand it does
   * not mention -- so the direction was overwritten a line later and had no
   * effect at all. Direction lives in the keyframe name instead, which is what
   * was actually working.
   */
  useEffect(() => {
    const updateBars = (bars, data) => {
      bars.forEach((bar, index) => {
        const entry = data[index];
        if (!entry) return;

        const arm = POWER_FLOW_ARMS[entry.title];
        const leg = arm?.[entry.flowDirection];

        /* No flow this period -- or a card the table does not cover -- shows no
         * arrow rather than an arbitrary one. */
        if (!leg) {
          bar.style.animationName = "none";
          bar.style.opacity = "0";
          return;
        }

        bar.style.opacity = "1";
        bar.style.background = flowGradient(arm.colour, leg.travel);
        bar.style.animation = `${leg.keyframes} 3s linear infinite`;
      });
    };

    /* DOM order within each line: transfer then production on the vertical arm,
     * consumption then storage on the horizontal one. summaryData is ordered
     * Transfer, Production, Consumption, Storage, so the slices line up. */
    updateBars(
      document.querySelectorAll(".vertical-bar"),
      summaryData.slice(0, 2),
    );
    updateBars(
      document.querySelectorAll(".horizontal-bar"),
      summaryData.slice(2, 4),
    );
  }, [summaryData]);

  if (!dataReady) {
    return <StatusCardSkeleton />;
  }

  // JSX structure
  return (
    <div className="status-card-grid">
      {summaryData.map((item, index) => {
        return (
          <div
            key={index}
            className={`status-card-grid-item shadow ${item.title.toLowerCase()} ${
              !interactive
                ? "summary-card--static"
                : summaryCardVisibility?.[item.title] === false
                ? "summary-card--inactive"
                : "summary-card--active"
            }`}
            data-testid={`summary-card-${toTestId(item.title)}`}
            data-card-title={item.title}
            data-card-surface={interactive ? "primary" : "overview"}
            data-card-active={
              !interactive || summaryCardVisibility?.[item.title] !== false
                ? "true"
                : "false"
            }
            role={interactive ? "button" : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-pressed={
              interactive
                ? summaryCardVisibility?.[item.title] === false
                  ? "false"
                  : "true"
                : undefined
            }
            onPointerDown={
              interactive
                ? (event) => {
                    onCardPointerDown?.(item.title, event.currentTarget);
                    event.preventDefault();
                  }
                : undefined
            }
            onMouseDown={interactive ? (event) => event.preventDefault() : undefined}
            onMouseEnter={interactive ? () => onCardHoverChange?.(item.title) : undefined}
            onMouseLeave={interactive ? () => onCardHoverChange?.(null) : undefined}
            onClick={
              interactive
                ? (event) => {
                    if (event.detail !== 0) {
                      event.currentTarget.blur?.();
                    }
                    onCardToggle?.(item.title);
                  }
                : undefined
            }
            onKeyDown={
              interactive
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onCardPointerDown?.(item.title, event.currentTarget);
                      onCardToggle?.(item.title);
                    }
                  }
                : undefined
            }
          >
            <div className="status-card-content">
              <div className="graph-name d-flex">
                <div className="sc-card-title">
                  {/* The label only. Everything else on this card keys off
                    * `item.title`, which stays "Transfer" -- see
                    * cardFlowRows.js. */}
                  <span className="graph-title">{item.displayTitle}</span>
                </div>
                <div className="sc-card-icon">
                  <div
                    className={`icon-circle ${item.iconBg} d-flex justify-content-center align-items-center`}
                  >
                    <i
                      className={item.iconClass}
                      style={{ color: item.iconColor }}
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>
              </div>

              <CardFlowRows
                rows={item.rows}
                total={item.price}
                totalIncludesDemand={item.priceIncludesDemand}
                totalLabel={item.totalLabel}
                reconciliation={item.reconciliation}
                testIdPrefix={`summary-card-${toTestId(item.title)}`}
                showColumnHeaders
              />
            </div>
          </div>
        );
      })}
      {/* Center Box */}
      <div className="center-box">
        <div className="center-content"></div>
      </div>
      <div className="vertical-line">
        <div className="moving-bar-transfer vertical-bar"></div>
        <div className="moving-bar-production vertical-bar"></div>
      </div>
      <div className="vertical-line-btm"></div>

      <div className="horizontal-line">
        <div className="moving-bar-consumption horizontal-bar"></div>
        <div className="moving-bar-storage horizontal-bar"></div>
      </div>
      <div className="horizontal-line-btm"></div>
    </div>
  );
}
